import React from 'react';

import { showErrorBar } from 'actions/errorBar';
import { updateWorkspaceDetails } from 'actions/workspace';
import { ERROR } from 'constants/error-severities';
import {
  PERSONAL_LIBRARY_INTRO_CARD,
  RECORD_VIDEO_CARD_KEY,
  SHARED_LIBRARY_INTRO_CARD,
  TEAM_LIBRARY_INTRO_CARD,
  WORKSPACE_ELEMENT_COMPLETE,
  WORKSPACE_ELEMENT_DISMISSED,
} from 'constants/workspaceOnboarding';
import {
  PRIVATE_WORKSPACE,
  SHARED_WORKSPACE,
  TEAM_WORKSPACE,
} from 'constants/workspaces';
import { getGraphQLClient } from 'utilities/graphql';
import * as sentry from 'utilities/sentry';

import markOnboardingCardAsComplete from './MarkOnboardingCardAsComplete.graphql';
import { mapCardKeytoComponent } from './constants';
import { getPersonalLibraryCardsConfig } from './ordered-card-sets/personal';
import { getSharedLibraryCardsConfig } from './ordered-card-sets/shared';
import { getTeamLibraryCardsConfig } from './ordered-card-sets/team';

const filterIneligibleCards = (onboardingCards, onboardingState) => {
  const _filterDismissedCards = card => {
    const [cardKey] = Object.keys(card);

    // there is no completed state anymore,
    // adjust this if we bring it back
    return [WORKSPACE_ELEMENT_DISMISSED, WORKSPACE_ELEMENT_COMPLETE].includes(
      onboardingState[cardKey]
    )
      ? null
      : cardKey;
  };

  const _applyCustomFilters = card => {
    const [cardValue] = Object.values(card);

    // if there is no filter, default to showing the card
    return cardValue?.filter ? cardValue.filter() : () => true;
  };

  const resultCards = [];

  onboardingCards.cards.forEach(cardItem => {
    const isCardArray = Array.isArray(cardItem);
    const item = isCardArray ? cardItem : [cardItem];

    if (resultCards.length > 1) {
      return null;
    }

    const filtered = item
      .filter(_filterDismissedCards)
      .filter(_applyCustomFilters);

    if (filtered.length > 0) {
      resultCards.push(filtered);
    }
  });

  return resultCards.flat();
};

const mapEligibleCardsToComponents = (onboardingState, onboardingCards) => {
  return filterIneligibleCards(onboardingCards, onboardingState)
    .map(card => {
      const [cardKey] = typeof card === 'string' ? [card] : Object.keys(card);
      const CardElement = mapCardKeytoComponent[cardKey];

      return (
        CardElement && <CardElement key={cardKey} cardConfig={card[cardKey]} />
      );
    })
    .filter(Boolean);
};

export const markEligibleElementAsCompleteForSelectedWorkspace = (
  dispatch,
  workspaces,
  cardKey,
  force = false
) => {
  const selectedWorkspace = workspaces?.find(workspace => workspace.isSelected);

  if (!selectedWorkspace) {
    return;
  }

  if (
    force ||
    canMarkElementAsComplete(selectedWorkspace.onboarding, cardKey)
  ) {
    completeElementHandler(dispatch, cardKey, selectedWorkspace.id);
  }
};

export const canMarkElementAsComplete = (onboarding, cardKey) => {
  const cardState = onboarding[cardKey];

  return ![WORKSPACE_ELEMENT_COMPLETE, WORKSPACE_ELEMENT_DISMISSED].includes(
    cardState
  );
};

export const completeElementHandler = async (
  dispatch,
  cardKey,
  workspaceId
) => {
  if (!cardKey || !dispatch) {
    return;
  }

  const gqlClient = getGraphQLClient();

  try {
    const { data } = await gqlClient.mutate({
      mutation: markOnboardingCardAsComplete,
      variables: {
        cardKey,
        workspaceId,
      },
    });

    const { onboarding } = data;

    dispatch(
      updateWorkspaceDetails({
        id: workspaceId,
        onboarding,
      })
    );
  } catch (err) {
    dispatch(showErrorBar(ERROR, 'Oops! Failed to dismisss onboarding card.'));

    sentry.logException(err, 'Error dismissing workplace onboarding card');
  }
};

export const getPersonalLibraryCards = ({
  isAdmin,
  hasRecordingExperience,
  userCanCustomizeSharePage,
}) => {
  const onboardingCards = getPersonalLibraryCardsConfig({
    isAdmin,
    hasRecordingExperience,
    userCanCustomizeSharePage,
  });

  onboardingCards.cards = [...onboardingCards.cards];

  return onboardingCards;
};

export const getSharedLibraryCards = () => {
  const onboardingCards = getSharedLibraryCardsConfig();

  onboardingCards.cards = [...onboardingCards.cards];

  return onboardingCards;
};

export const getTeamLibraryCards = ({ userHasVideosInTeamLibrary }) => {
  const onboardingCards = getTeamLibraryCardsConfig({
    userHasVideosInTeamLibrary,
  });

  onboardingCards.cards = [...onboardingCards.cards];

  return onboardingCards;
};

export const isIntroCard = cardKey => {
  return [
    PERSONAL_LIBRARY_INTRO_CARD,
    SHARED_LIBRARY_INTRO_CARD,
    TEAM_LIBRARY_INTRO_CARD,
  ].includes(cardKey);
};

export const isAutoCompleteExcluded = (cardConfig, cardKey) => {
  return isIntroCard(cardKey) || !cardConfig?.dismissable;
};

export const cardHasBeenCompleted = (onboardingState, cardKey) => {
  return [WORKSPACE_ELEMENT_COMPLETE, WORKSPACE_ELEMENT_DISMISSED].includes(
    onboardingState[cardKey]
  );
};

export const cardHasBeenDismissed = (onboardingState, cardKey) => {
  return onboardingState[cardKey] === WORKSPACE_ELEMENT_DISMISSED;
};

export const fadeOutCard = (dispatch, card, fade, show, dismiss) => {
  // Fade and dismiss card after 3 seconds if it's complete
  setTimeout(() => {
    fade(true);

    // Let the fade animation play for half a second, then dismiss card
    setTimeout(() => {
      show(false);
      dismiss();

      if (card.onDismiss) {
        card.onDismiss(dispatch);
      }
    }, 500);
  }, 3000);

  if (card.onComplete) {
    card.onComplete(dispatch);
  }
};

export const getDashboardOnboardingCards = ({
  library,
  isAdmin,
  onboardingState,
  userCanCustomizeSharePage,
  teamLibraryVideos = [],
}) => {
  let onboardingCards = null;

  const hasRecordingExperience = cardHasBeenCompleted(
    onboardingState,
    RECORD_VIDEO_CARD_KEY
  );

  const userHasVideosInTeamLibrary =
    teamLibraryVideos?.filter(v => v.currentUserIsOwner).length > 0;

  switch (library) {
    case TEAM_WORKSPACE:
      onboardingCards = getTeamLibraryCards({
        userHasVideosInTeamLibrary,
      });
      break;
    case SHARED_WORKSPACE:
      onboardingCards = getSharedLibraryCards();
      break;
    case PRIVATE_WORKSPACE:
      onboardingCards = getPersonalLibraryCards({
        isAdmin,
        hasRecordingExperience,
        userCanCustomizeSharePage,
      });
      break;
    default:
      onboardingCards = [];
  }

  return mapEligibleCardsToComponents(onboardingState, onboardingCards);
};
